/**
 * Endpoint → OpenAPI projection.
 *
 * An Innovate endpoint maps almost one-to-one onto a single OpenAPI operation:
 * `path` + `method` are the operation key, `description` is the summary, `input`
 * is the request (a body for POST/PUT/PATCH, query parameters for GET/DELETE),
 * and `output` is the 200 response. `input`/`output` are already JSON Schema, and
 * OpenAPI 3.1 is a JSON-Schema superset, so the schemas embed directly.
 *
 * This is a pure, client-side projection built from an already-fetched Endpoint —
 * it makes no network call and does not consume the service's `/openapi.json`.
 */

import type { Endpoint, AuthPolicyMode } from '@/types';
import { BARE_URL_ENVIRONMENT_PATH } from '@/types/environments';
import type { Environment } from '@/types/environments';
import {
  apiServerUrl,
  bareEndpointPath,
  customDomainServerUrl,
  endpointDisplayPath,
  environmentServerUrl,
} from './endpointUrl';
import { jsonSchemaToFields, type FieldDefinition } from './schemaBuilder';

// --- OpenAPI document shape (only the parts we emit) ----------------------

export interface OpenApiParameter {
  name: string;
  in: 'query';
  required: boolean;
  description?: string;
  schema: Record<string, unknown>;
}

export interface OpenApiMediaType {
  schema: Record<string, unknown>;
}

export interface OpenApiRequestBody {
  required: boolean;
  content: Record<string, OpenApiMediaType>;
}

export interface OpenApiResponse {
  description: string;
  content?: Record<string, OpenApiMediaType>;
}

export interface OpenApiOperation {
  summary?: string;
  description?: string;
  operationId: string;
  parameters?: OpenApiParameter[];
  requestBody?: OpenApiRequestBody;
  responses: Record<string, OpenApiResponse>;
  security?: Array<Record<string, string[]>>;
  'x-innovate-mcp-exposed': boolean;
  'x-innovate-response-queued': boolean;
}

export interface OpenApiSecurityScheme {
  type: 'apiKey' | 'http';
  in?: 'header';
  name?: string;
  scheme?: string;
  bearerFormat?: string;
  description?: string;
}

export interface OpenApiDocument {
  openapi: '3.1.0';
  info: { title: string; description?: string; version: string };
  servers?: Array<{ url: string; description?: string }>;
  paths: Record<string, Record<string, OpenApiOperation>>;
  components?: {
    schemas?: Record<string, Record<string, unknown>>;
    securitySchemes?: Record<string, OpenApiSecurityScheme>;
  };
}

// --- Helpers --------------------------------------------------------------

const BODY_METHODS = new Set(['POST', 'PUT', 'PATCH']);

/** Innovate endpoints are served under `${API_URL}/api`. */
const serverUrl = apiServerUrl;

/**
 * The OpenAPI `paths` key for an endpoint: `/{accountSlug}/{path}` (task 385).
 *
 * The server URL stays `${API_URL}/api`, so the account slug belongs in the
 * path key — that is what makes Swagger's try-it-out button and the
 * copied/exported `openapi.json` hit the real, resolvable URL. Stored paths are
 * inconsistent about the leading slash, and the shared helper normalizes it so
 * joins never collapse (`/api` + `foo` → `/apifoo`) or double up. Rows without
 * a slug fall back to the legacy root shape.
 */
function normalizePath(endpoint: Pick<Endpoint, 'path'> & { accountSlug?: string | null }): string {
  return endpointDisplayPath(endpoint);
}

/**
 * Build a PascalCase token from the path for schema/operation naming, dropping
 * `{param}` segments. `/orders/{id}/items` → `OrdersItems`; falls back to
 * `Endpoint` when nothing usable remains.
 */
function pathToName(path: string): string {
  const token = path
    .split('/')
    .filter(seg => seg && !seg.startsWith('{'))
    .map(seg => seg.replace(/[^a-zA-Z0-9]+/g, ' '))
    .join(' ')
    .split(' ')
    .filter(Boolean)
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join('');
  return token || 'Endpoint';
}

/** Map an endpoint auth policy to an OpenAPI security scheme, or null for public. */
function securitySchemeFor(
  mode: AuthPolicyMode
): { key: string; scheme: OpenApiSecurityScheme } | null {
  switch (mode) {
    case 'apiKey':
      return {
        key: 'ApiKeyAuth',
        scheme: { type: 'apiKey', in: 'header', name: 'X-API-Key' },
      };
    case 'jwt':
      return {
        key: 'BearerAuth',
        scheme: { type: 'http', scheme: 'bearer', bearerFormat: 'JWT' },
      };
    case 'custom':
      return {
        key: 'CustomAuth',
        scheme: {
          type: 'apiKey',
          in: 'header',
          name: 'Authorization',
          description: 'Custom authentication policy defined for this endpoint.',
        },
      };
    case 'public':
    default:
      return null;
  }
}

/** Turn one input-schema property into an OpenAPI query parameter. */
function propertyToParameter(
  name: string,
  prop: Record<string, unknown>,
  required: boolean
): OpenApiParameter {
  const { description, ...schema } = prop as { description?: string };
  return {
    name,
    in: 'query',
    required,
    ...(description ? { description } : {}),
    schema: schema as Record<string, unknown>,
  };
}

/** Placeholder value for a field, used to seed the curl example body. */
function sampleValue(field: FieldDefinition): unknown {
  if (field.defaultValue !== undefined) return field.defaultValue;
  if (field.enumValues && field.enumValues.length > 0) return field.enumValues[0];
  switch (field.type) {
    case 'number':
    case 'integer':
      return 0;
    case 'boolean':
      return false;
    case 'string':
    default:
      if (field.format === 'email') return 'user@example.com';
      if (field.format === 'uri') return 'https://example.com';
      if (field.format === 'date') return '2026-01-01';
      if (field.format === 'date-time') return '2026-01-01T00:00:00Z';
      return '';
  }
}

// --- Projection -----------------------------------------------------------

/**
 * The `servers` array, and the `paths` key that pairs with it (task 799).
 *
 * With no environments this is exactly what it always was: one server at
 * `${API_URL}/api`, and the account slug in the path key.
 *
 * With environments, the document has to offer a server per stage so Swagger's
 * picker can switch between them — and the environment segment sits BETWEEN the
 * slug and the path (`/api/{slug}/{env}/{path}`), which a server prefix cannot
 * express while the slug stays in the key. So the slug moves into every server,
 * the key becomes the bare endpoint path, and each server + key resolves to the
 * real, callable URL. Operation ids and schema names are unaffected: they were
 * already derived from the bare path, never from the key.
 */
function buildServersAndPathKey(
  endpoint: Endpoint,
  environments: Environment[],
  publishingEnabled: boolean,
  customDomainHostnames: string[]
): { servers: OpenApiDocument['servers']; pathKey: string } {
  /**
   * A custom-domain server is a PREFIX with no slug in it (task 857), so it can
   * only pair with the bare path key — the same split the environment servers
   * already force. Hence the two zero-environment cases: with no domains this
   * is byte-for-byte the single-server document it always was, and with one it
   * moves the slug into the platform server so every server + key in the
   * document still resolves to a real URL.
   */
  if (environments.length === 0) {
    if (customDomainHostnames.length === 0) {
      return { servers: [{ url: serverUrl() }], pathKey: normalizePath(endpoint) };
    }
    return {
      servers: [
        // PLATFORM FIRST, deliberately: Swagger's Try it out runs against the
        // first server, and this document is read INSIDE the app — an in-app
        // call is a call the platform makes to itself, so it takes the address
        // the platform owns. The custom domain is offered, never defaulted.
        {
          url: environmentServerUrl(endpoint, null),
          description: 'Platform URL',
        },
        ...customDomainHostnames.map(hostname => ({
          url: customDomainServerUrl(hostname),
          description: `${hostname} — your custom domain`,
        })),
      ],
      pathKey: `/${bareEndpointPath(endpoint.path)}`,
    };
  }

  // ADDRESSED BY PATH, LABELLED BY NAME (task 831). A server entry is a URL, so
  // it takes the environment's path; the description is what a reader picks
  // from a list, so it takes the name. The environment holding `/` gets NO entry
  // of its own — it has no segment, so its address IS the plain URL above, and a
  // second identical server would only make the picker ambiguous. It is named
  // there instead.
  const bareHolder = environments.find(
    environment => environment.path === BARE_URL_ENVIRONMENT_PATH
  );

  return {
    servers: [
      {
        url: environmentServerUrl(endpoint, null),
        description: publishingEnabled
          ? bareHolder
            ? `Plain URL — serves the ${bareHolder.name} environment’s deployed version`
            : 'Plain URL — serves nothing until an environment takes the path “/”'
          : 'Plain URL — serves the working state',
      },
      // Promotion order, the order every environments surface reads in.
      ...[...environments]
        .filter(environment => environment.path !== BARE_URL_ENVIRONMENT_PATH)
        .sort((a, b) => a.order - b.order)
        .map(environment => ({
          url: environmentServerUrl(endpoint, environment.path),
          description: `${environment.name} environment`,
        })),
      // THE SAME LADDER ON EACH CUSTOM DOMAIN, AFTER ALL OF IT (task 857). The
      // plain address then one per stage, labelled by hostname so the picker
      // says which domain a reader is choosing — and last, so the platform
      // servers stay the default the in-app Try it out uses.
      ...customDomainHostnames.flatMap(hostname => [
        {
          url: customDomainServerUrl(hostname),
          description: `${hostname} — your custom domain`,
        },
        ...[...environments]
          .filter(environment => environment.path !== BARE_URL_ENVIRONMENT_PATH)
          .sort((a, b) => a.order - b.order)
          .map(environment => ({
            url: customDomainServerUrl(hostname, environment.path),
            description: `${hostname} — ${environment.name} environment`,
          })),
      ]),
    ],
    pathKey: `/${bareEndpointPath(endpoint.path)}`,
  };
}

/**
 * Project an endpoint into a single-operation OpenAPI 3.1 document.
 *
 * `environments` (task 799) adds one `servers` entry per deployment
 * environment. Omitting it — which is what every caller for an Account without
 * environments does — produces byte-for-byte the document it always did.
 *
 * `publishingEnabled` (task 853) is what the plain server's description is
 * written from: whether the ACCOUNT has Publishing on, which is what decides
 * whether the environments serve released versions of this AI Service or it
 * serves its working state. It is an
 * ARGUMENT rather than anything read off the endpoint row because this function
 * is pure and its caller — `ApiDocsPanel` — is the one holding the account
 * setting. There is no per-service serving mode to read: the endpoint carries
 * no such field at all any more (task 869).
 *
 * `customDomainHostnames` (task 857) appends one server per hostname the account
 * is serving on, AFTER the platform servers — this document is read inside the
 * app, where Try it out is the platform calling itself, so the platform stays
 * the default and the domain is an option a reader picks. Passing none (every
 * account without a custom domain) leaves the document exactly as it was.
 */
export function endpointToOpenApiDocument(
  endpoint: Endpoint,
  environments: Environment[] = [],
  publishingEnabled = false,
  customDomainHostnames: string[] = []
): OpenApiDocument {
  const method = endpoint.method.toLowerCase();
  // The slug-qualified address a reader recognizes, used for the human-facing
  // summary/title regardless of how the servers/path split lands below.
  const path = normalizePath(endpoint);
  const { servers, pathKey } = buildServersAndPathKey(
    endpoint,
    environments,
    publishingEnabled,
    customDomainHostnames
  );
  // Schema/operation names come from the endpoint path ALONE — deliberately not
  // the slug-qualified key, so adding the account prefix didn't rename every
  // generated schema (`TicketTriageInput`, not `AcmeTicketTriageInput`).
  const name = pathToName(bareEndpointPath(endpoint.path));
  const mediaType = endpoint.requestFormat || 'application/json';
  const isBodyMethod = BODY_METHODS.has(endpoint.method);
  const summary = endpoint.description?.trim() || `${endpoint.method} ${path}`;

  const schemas: Record<string, Record<string, unknown>> = {};
  const inputSchemaName = `${name}Input`;
  const outputSchemaName = `${name}Output`;

  const operation: OpenApiOperation = {
    summary,
    operationId: `${method}${name}`,
    responses: {},
    'x-innovate-mcp-exposed': endpoint.mcpExposed,
    'x-innovate-response-queued': endpoint.responseQueued,
  };

  // Request: body for POST/PUT/PATCH, query parameters for GET/DELETE.
  if (endpoint.input) {
    if (isBodyMethod) {
      schemas[inputSchemaName] = endpoint.input;
      operation.requestBody = {
        required: true,
        content: {
          [mediaType]: { schema: { $ref: `#/components/schemas/${inputSchemaName}` } },
        },
      };
    } else {
      const properties = (endpoint.input.properties ?? {}) as Record<
        string,
        Record<string, unknown>
      >;
      const required = (endpoint.input.required as string[] | undefined) ?? [];
      const params = Object.entries(properties).map(([propName, prop]) =>
        propertyToParameter(propName, prop, required.includes(propName))
      );
      if (params.length > 0) {
        operation.parameters = params;
      }
    }
  }

  // Response: 200 with the output schema, or a described empty response.
  if (endpoint.output) {
    schemas[outputSchemaName] = endpoint.output;
    operation.responses['200'] = {
      description: 'Successful response',
      content: {
        [mediaType]: { schema: { $ref: `#/components/schemas/${outputSchemaName}` } },
      },
    };
  } else {
    operation.responses['200'] = {
      description: 'Successful response (no output schema defined)',
    };
  }

  // Security from the auth policy (public → none).
  const security = securitySchemeFor(endpoint.authPolicy?.mode ?? 'public');
  const securitySchemes: Record<string, OpenApiSecurityScheme> = {};
  if (security) {
    securitySchemes[security.key] = security.scheme;
    operation.security = [{ [security.key]: [] }];
  }

  const components: OpenApiDocument['components'] = {};
  if (Object.keys(schemas).length > 0) components.schemas = schemas;
  if (Object.keys(securitySchemes).length > 0) components.securitySchemes = securitySchemes;

  const doc: OpenApiDocument = {
    openapi: '3.1.0',
    info: {
      title: summary,
      ...(endpoint.description?.trim() ? { description: endpoint.description.trim() } : {}),
      version: '1.0.0',
    },
    servers,
    paths: {
      [pathKey]: { [method]: operation },
    },
  };

  if (components.schemas || components.securitySchemes) {
    doc.components = components;
  }

  return doc;
}

/** Pretty-printed JSON serialization of the OpenAPI document. */
export function openApiToJson(doc: OpenApiDocument): string {
  return JSON.stringify(doc, null, 2);
}

/**
 * Generate a runnable `curl` example for the endpoint. Body methods get a
 * sample JSON body seeded from the input schema; GET/DELETE inputs become query
 * parameters. The auth policy adds the matching header.
 */
export function endpointToCurl(endpoint: Endpoint): string {
  const mediaType = endpoint.requestFormat || 'application/json';
  const isBodyMethod = BODY_METHODS.has(endpoint.method);
  const fields = jsonSchemaToFields(endpoint.input);

  let url = `${serverUrl()}${normalizePath(endpoint)}`;

  // GET/DELETE: append input fields as a query string.
  if (!isBodyMethod && fields.length > 0) {
    const query = fields
      .map(f => `${encodeURIComponent(f.name)}=${encodeURIComponent(String(sampleValue(f)))}`)
      .join('&');
    url = `${url}?${query}`;
  }

  const lines: string[] = [`curl -X ${endpoint.method} "${url}"`];

  // Auth header.
  const mode = endpoint.authPolicy?.mode ?? 'public';
  if (mode === 'apiKey') {
    lines.push(`  -H "X-API-Key: $API_KEY"`);
  } else if (mode === 'jwt') {
    lines.push(`  -H "Authorization: Bearer $TOKEN"`);
  } else if (mode === 'custom') {
    lines.push(`  -H "Authorization: $CREDENTIALS"`);
  }

  // Body for POST/PUT/PATCH.
  if (isBodyMethod) {
    lines.push(`  -H "Content-Type: ${mediaType}"`);
    const body: Record<string, unknown> = {};
    for (const field of fields) body[field.name] = sampleValue(field);
    lines.push(`  -d '${JSON.stringify(body)}'`);
  }

  return lines.join(' \\\n');
}
